import { cloneDeep } from "lodash";
import { showToast } from "utils/toast";
import useDebounce from "app/hooks/useDebounce";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import {
  changeProfileField,
  setPhoneOptOutInformation,
} from "app/store/transmission/slice/transmissionSlice";
import { useSaveInformationMutation } from "app/store/transmission/api/transmissionTableApi";
import { trimStringValuesInObject } from "utils/object";

const usePatientInformation = () => {
  const {
    profile,
    physicians,
    information,
    patientSerialNumber,
    originalInformation,
    phoneOptOutInformation,
  } = useSelector((state) => state.transmission);

  const [newPatientInformation, setNewPatientInformation] = useState({
    phone1: "",
    phone2: "",
    email: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
    emergency_contact: {
      phone: "",
      name: "",
      relation: "",
    },
  });

  useEffect(() => {
    if (information) {
      const copy = cloneDeep(information);
      delete copy["anticoagStatus"];
      delete copy["anticoagType"];
      delete copy["visit_location"];

      setNewPatientInformation({
        ...copy,
      });
    }
  }, [information]);

  useEffect(() => {
    if (
      originalInformation &&
      originalInformation.anticoagStatus === undefined
    ) {
      setNewPatientInformation((prevState) => ({
        ...prevState,
        anticoagStatus: getAnticoagStatus(originalInformation.anticoag),
      }));
    }
  }, [originalInformation]);

  /**
   * Return anticoag status
   * if "anticoag" key doesn't exist, return "unknown"
   * else set true or false accordingly
   * @param {*} anticoag
   * @returns anticoagStatus
   */
  const getAnticoagStatus = (anticoag) => {
    if (anticoag === undefined) return "unknown";
    if (!anticoag) return false;
    return true;
  };

  const dispatch = useDispatch();

  const [saveInformation, { isSuccess, isError, error, data: updatedData }] =
    useSaveInformationMutation();

  useEffect(() => {
    isError && error && showToast({ message: error?.message, type: "error" });
    if (isSuccess && updatedData) {
      showToast({ message: updatedData?.message });
    }
  }, [updatedData, error]);

  useEffect(() => {
    const findOptOut = (phoneNumber) =>
      profile?.optOutPhoneNumbers?.find(
        (entry) => entry.phone === phoneNumber
      ) ?? null;

    const phoneOptOutInformation = {
      primaryNumberOptOut: findOptOut(newPatientInformation?.phone1),
      secondaryNumberOptOut: findOptOut(newPatientInformation?.phone2),
      emergencyPhoneOptOut: findOptOut(
        newPatientInformation?.emergency_contact?.phone
      ),
    };

    dispatch(setPhoneOptOutInformation(phoneOptOutInformation));
  }, [newPatientInformation]);

  const handleInformation = async (values) => {
    const informationPayload = trimStringValuesInObject(values);

    const response = await saveInformation({
      serialNumber: patientSerialNumber,
      information: informationPayload,
    });

    if (!response.error) {
      const updatedValue = cloneDeep(information);
      dispatch(
        changeProfileField({
          type: "originalInformation",
          value: updatedValue,
        })
      );
    }

    return response;
  };

  const debounceDispatch = useCallback(
    useDebounce((value, type) => {
      dispatch(
        changeProfileField({
          type: type ?? "information",
          value: value,
        })
      );
    }, 500),
    []
  );

  const handleCancel = () => {
    const copy = cloneDeep(originalInformation);
    handleInputChange(copy);
  };

  const handleInputChange = async (updatedInformation) => {
    setNewPatientInformation({
      ...updatedInformation,
    });
    debounceDispatch(updatedInformation);
  };

  return {
    profile,
    dispatch,
    physicians,
    information,
    handleCancel,
    getAnticoagStatus,
    handleInputChange,
    handleInformation,
    phoneOptOutInformation,
    newPatientInformation,
  };
};

export default usePatientInformation;
